import { VFC } from 'react';
import { FormError } from 'shared/components/FormControl';

import { CustomLocation } from 'dashboard/modules/Location/model';
import Button from 'design-system/components/Button';
import Input from 'design-system/components/Input';
import Spacer from 'design-system/components/Spacer';
import { HStack } from 'design-system/components/Stack';
import { Label } from 'design-system/components/Text';
import { useFormSecurely } from 'lib/security/reactHookForm';

interface FormEditCustomLocationProps {
  defaultValues: Partial<FormEditCustomLocationData>;
  handleUpdateCustomLocation: (updatedCustomLocation: CustomLocation) => void;
  handleClose: () => void;
}

const FormEditCustomLocation: VFC<FormEditCustomLocationProps> = ({
  defaultValues,
  handleUpdateCustomLocation,
  handleClose,
}) => {
  const {
    register,
    handleSubmit,
    formState: { isSubmitting, errors, isValid },
  } = useFormSecurely<FormEditCustomLocationData>({
    defaultValues,
    mode: 'onChange',
  });

  return (
    <form onSubmit={handleSubmit(handleUpdateCustomLocation)}>
      <Label id='form-edit-custom-location__name_label' size='md' weight='bold'>
        Custom location name
      </Label>
      <Input
        {...register('name', {
          required: 'Custom locations must have a name.',
          pattern: {
            value: /^[_a-zA-Z0-9][_a-zA-Z0-9\s]*$/,
            message: 'Please use letters, numbers, underscores, or spaces only.',
          },
        })}
        disabled={isSubmitting}
        role='input'
        aria-labelledby='form-edit-custom-location__name_label'
      />
      {errors['name'] && <FormError>{errors['name'].message}</FormError>}

      <Spacer height={1} />

      <Label id='form-edit-custom-location__latitude_label' size='md' weight='bold'>
        Latitude
      </Label>
      <Input
        {...register('latitude', {
          required: 'Custom locations must have a latitude.',
          valueAsNumber: true,
          min: {
            value: -90,
            message: 'Latitude must be between -90 and 90.',
          },
          max: {
            value: 90,
            message: 'Latitude must be between -90 and 90.',
          },
        })}
        disabled={isSubmitting}
        role='input'
        type='number'
        step={0.0001}
        aria-labelledby='form-edit-custom-location__latitude_label'
      />
      {errors['latitude'] && <FormError>{errors['latitude'].message}</FormError>}

      <Spacer height={1} />

      <Label id='form-edit-custom-location__longitude_label' size='md' weight='bold'>
        Longitude
      </Label>
      <Input
        {...register('longitude', {
          required: 'Custom locations must have a longitude.',
          valueAsNumber: true,
          min: {
            value: -180,
            message: 'Longitude must be between -180 and 180.',
          },
          max: {
            value: 180,
            message: 'Longitude must be between -180 and 180.',
          },
        })}
        disabled={isSubmitting}
        role='input'
        type='number'
        step={0.0001}
        aria-labelledby='form-edit-custom-location__longitude_label'
      />
      {errors['longitude'] && <FormError>{errors['longitude'].message}</FormError>}

      <Spacer height={1} />

      <Label id='form-edit-custom-location__radius-meters_label' size='md' weight='bold'>
        Radius (meters)
      </Label>
      <Input
        {...register('radius-meters', {
          required: 'Custom locations must have a radius.',
          valueAsNumber: true,
          min: {
            value: 0,
            message: 'Radius must be greater than 0.',
          },
        })}
        disabled={isSubmitting}
        role='input'
        type='number'
        step={1}
        aria-labelledby='form-edit-custom-location__radius-meters_label'
      />
      {errors['radius-meters'] && <FormError>{errors['radius-meters'].message}</FormError>}

      <Spacer height={2} />

      <HStack equal={true}>
        <Button
          id='form-edit-custom-location__cancel_button'
          variant='basic'
          disabled={isSubmitting}
          isPending={isSubmitting}
          onClick={handleClose}
        >
          Cancel
        </Button>
        <Button
          variant='primary'
          type='submit'
          disabled={isSubmitting || !isValid}
          isPending={isSubmitting}
        >
          Update
        </Button>
      </HStack>
    </form>
  );
};

export interface FormEditCustomLocationData {
  name: string;
  latitude: number;
  longitude: number;
  'radius-meters': number;
}

export default FormEditCustomLocation;
